import type { IntlShape } from 'react-intl';
import type { PeopleRow, SentPitch } from '../../outreachTypes';
import { listenDuration } from '../liveFormat';
import { plannedWhen } from '../peopleRowCopy';
import { detailsMessages as m } from './detailsMessages';
import { longestListen, newestReply } from './pitchTimeline';

export type HeroTone = 'plain' | 'vio' | 'pos';

/** The headline of the status card (design outreach-v2 t2, t3, t4): the strongest news first. */
export interface PitchHero {
  tone: HeroTone;
  title: string;
  sub: string | null;
  /** "Answer in Inbox" opens this reply. */
  replyUuid: string | null;
  /** "Read the pitches" opens the review of the campaign. */
  review: boolean;
}

function shortDate(intl: IntlShape, iso: string): string {
  return intl.formatDate(new Date(iso), { month: 'short', day: 'numeric' });
}

export function pitchHero(intl: IntlShape, row: PeopleRow, pitch: SentPitch | undefined, name: string): PitchHero {
  const none = { sub: null, replyUuid: null, review: false };
  if (row.result === 'scheduled') {
    if (row.plannedState === 'writing') {
      return { ...none, tone: 'plain', title: intl.formatMessage(m.heroWriting) };
    }
    if (row.plannedState === 'review') {
      return {
        ...none,
        tone: 'vio',
        title: intl.formatMessage(m.heroReview),
        sub: intl.formatMessage(m.heroReviewSub),
        review: true,
      };
    }
    const when = plannedWhen(intl, row);
    return {
      ...none,
      tone: 'plain',
      title: when === null ? intl.formatMessage(m.heroPlannedNoTime) : intl.formatMessage(m.heroPlanned, { when }),
      sub: intl.formatMessage(m.heroPlannedSub),
    };
  }
  const reply = pitch === undefined ? null : newestReply(pitch);
  if (row.result === 'added') {
    return {
      ...none,
      tone: 'pos',
      title: intl.formatMessage(m.heroAdded, { name }),
      replyUuid: reply?.replyUuid ?? null,
    };
  }
  if (row.result === 'replied' || reply !== null) {
    return {
      ...none,
      tone: 'pos',
      title: intl.formatMessage(m.heroReplied, { name }),
      replyUuid: reply?.replyUuid ?? null,
    };
  }
  if (row.result === 'listened') {
    const seconds = pitch === undefined ? null : longestListen(pitch);
    return {
      ...none,
      tone: 'vio',
      title:
        seconds === null
          ? intl.formatMessage(m.stepOpened)
          : intl.formatMessage(m.heroListened, { duration: listenDuration(intl, seconds) }),
      sub: intl.formatMessage(m.heroNoReplySub),
    };
  }
  if (row.result === 'opened') {
    return { ...none, tone: 'vio', title: intl.formatMessage(m.heroOpened), sub: intl.formatMessage(m.heroNoReplySub) };
  }
  return {
    ...none,
    tone: 'vio',
    title: intl.formatMessage(m.heroWaiting),
    sub: intl.formatMessage(m.heroWaitingSub, { date: shortDate(intl, row.at) }),
  };
}
